import { OrderAction, OrderStatus } from "@prisma/client";
import { IconSelector } from "@tabler/icons-react";
import { forwardRef, useState } from "react";

import { useAuthorization } from "~/contexts/authorizationProvider";
import { BlockerResponseType, BlockerStatus } from "~/modules/blockers/types";
import { getRegulatoryFindingTooltip } from "~/shared/regulatory/regulatory-findings";
import { OrderPermission } from "~/shared/types/RBACPermission";
import { OrderRegulatoryFindings } from "~/shared/types/RegulatoryFinding";
import { Badge, BadgeProps, Button, MultiSelect, type MultiSelectProps, Select, type SelectProps, Text, Tooltip } from "~/ui";
import { enumTermToText, transforms } from "~/utils/strings";

import { ActionOrder, getAvailableAndAllowedOrderActions } from "./order-actions-text";
import { OrderActivityInputType } from "./order-forms";
import { getOrderStatusBlockerMessage } from "./order-transition-messages";

/** Public path kept for existing consumers; the single definition lives in `~/shared/regulatory`. */
export { getRegulatoryFindingTooltip };

/**
 * The findings the server sends describe exactly one transition: the forward hop out of the Order's
 * current status, resolved by `resolveOrderForwardStatusTransitionLocation`. Showing them on any
 * other option would attach a reason to the wrong control, and showing them on none but Submit —
 * which is what this did while Submit was the only place a rule could live — leaves Ship and
 * Deliver enabled with a Block the server will refuse.
 *
 * A total `Record<OrderStatus, …>`, so a new `OrderStatus` member is a compile error here rather
 * than a silently unguarded hop.
 */
const forwardStatusFor: Record<OrderStatus, OrderStatus | null> = {
  DRAFT: OrderStatus.CONFIRMED,
  CONFIRMED: OrderStatus.SHIPPED,
  SHIPPED: OrderStatus.DELIVERED,
  DELIVERED: null,
  COMPLETED: null,
  VOID: null,
};

const statusToColors: Record<OrderStatus, string> = {
  VOID: "gray",
  DRAFT: "gray",
  CONFIRMED: "blue",
  SHIPPED: "gray",
  DELIVERED: "gray",
  COMPLETED: "green",
};

const termToText = new Map<OrderStatus, string>();
Object.keys(OrderStatus).forEach(v => termToText.set(v as OrderStatus, enumTermToText(v, transforms("Po", "PO"))));

const statusOptions = Array.from(termToText).map(([term, text]) => ({ value: term, label: text }));

export const OrderStatusSelect = (props: Omit<SelectProps, "data">) => (
  <Select placeholder="Status" searchable clearable {...props} data={statusOptions} />
);

export const OrderStatusMultiSelect = (props: Omit<MultiSelectProps, "data">) => (
  <MultiSelect placeholder="Status" searchable clearable {...props} data={statusOptions} />
);

type OrderStatusDisplayProps = Omit<BadgeProps, "color"> & {
  status: OrderStatus;
};
export const OrderStatusDisplay = (props: OrderStatusDisplayProps) => (
  <Badge style={{ textTransform: "none" }} {...props} color={statusToColors[props.status]}>
    {termToText.get(props.status)}
  </Badge>
);

/** Status → the action that moves an order into it. At most one may be allowed for a given order. */
export const statusActions = [
  { status: OrderStatus.DRAFT, actions: [OrderAction.REVERSE_SUBMIT] },
  { status: OrderStatus.CONFIRMED, actions: [OrderAction.SUBMIT, OrderAction.REVERSE_SHIPPED] },
  { status: OrderStatus.SHIPPED, actions: [OrderAction.SHIP, OrderAction.REVERSE_MARK_DELIVERED] },
  { status: OrderStatus.DELIVERED, actions: [OrderAction.MARK_DELIVERED, OrderAction.REVERSE_MARK_COMPLETED] },
  { status: OrderStatus.COMPLETED, actions: [OrderAction.MARK_COMPLETED] },
  { status: OrderStatus.VOID, actions: [OrderAction.MARK_VOID] },
];

interface OrderStatusSelectItemProps extends React.ComponentPropsWithoutRef<"div"> {
  status: OrderStatus;
  information?: string;
}
const OrderStatusSelectItem = forwardRef<HTMLDivElement, OrderStatusSelectItemProps>(({ status, information, ...others }, ref) => {
  const labelComponent = <Text style={{ cursor: !!information ? "not-allowed" : "" }}>{termToText.get(status)}</Text>;
  if (!!information) {
    return (
      <div ref={ref} {...others}>
        <Tooltip label={information} arrowSize={8} multiline offset={-25} position="right" width={220} withArrow withinPortal>
          {labelComponent}
        </Tooltip>
      </div>
    );
  }
  return (
    <div ref={ref} {...others}>
      {labelComponent}
    </div>
  );
});
OrderStatusSelectItem.displayName = "OrderStatusSelectItem";

type ReopenOrderButtonProps = {
  orderId: string;
  orderStatus: OrderStatus;
  onClick: (orderId: string) => void;
};
export const ReopenOrderButton = (props: ReopenOrderButtonProps) => {
  if (props.orderStatus !== OrderStatus.VOID) {
    return <></>;
  }
  return (
    <Button size="xs" onClick={() => props.onClick(props.orderId)}>
      Reopen Order
    </Button>
  );
};

/**
 * Combines the real-blocker text (invoices, bills, external `blockedMessage`) with the regulatory
 * Block finding text for one status option. Only a real blocker disables the option — a regulatory
 * Block is shown as information so the user can still attempt the transition and let the server's
 * preflight refuse it (`WorkflowError`, order stays put) and write the notification on refusal.
 */
export const getOrderStatusOptionState = (
  isCurrent: boolean,
  isNeededActionAvailable: boolean,
  blockerInformation: string,
  regulatoryBlockedMessage = "",
): { disabled: boolean; information: string } => {
  const disabled = !isCurrent && (!isNeededActionAvailable || !!blockerInformation);
  const information =
    [blockerInformation, regulatoryBlockedMessage].filter(Boolean).join("; ") || (disabled ? "This transition is not available" : "");
  return { disabled, information };
};

interface OrderStatusSwitchProps {
  displayLabel?: boolean;
  order: ActionOrder;
  blockerData: BlockerResponseType;
  regulatoryFindings?: OrderRegulatoryFindings | null;
  setOrderActivity: (oa: OrderActivityInputType | undefined) => void;
}
export const OrderStatusSwitch = (props: OrderStatusSwitchProps) => {
  const { hasPermission } = useAuthorization();

  const [selectOpen, setSelectOpen] = useState(false);
  const availableActions = getAvailableAndAllowedOrderActions(props.order).map(a => a.action);
  const blockers = props.blockerData && props.blockerData.enabledBlockers && (props.blockerData.entries[props.order.id] ?? false);
  const blockedMessage =
    blockers && blockers.status === BlockerStatus.INCOMPLETE
      ? `${blockers.total - blockers.completed}/${blockers.total} checks failing`
      : "";
  const hasUpdatePermission = hasPermission([OrderPermission.update]);

  if (!hasUpdatePermission || props.displayLabel) {
    return <OrderStatusDisplay status={props.order.status} />;
  }

  const selectOptions = [
    OrderStatus.VOID,
    OrderStatus.DRAFT,
    OrderStatus.CONFIRMED,
    OrderStatus.SHIPPED,
    OrderStatus.DELIVERED,
    OrderStatus.COMPLETED,
  ].map(status => {
    const actions: OrderAction[] = statusActions.find(sa => sa.status === status)?.actions ?? [];
    const allowedActions = actions.filter(a => availableActions.includes(a));
    if (allowedActions.length > 1) {
      throw new Error("Should only be one allowed Order Action");
    }
    const neededAction = allowedActions[0];

    const isCurrent = status === props.order.status;
    const isNeededActionAvailable = !!neededAction;
    const regulatoryBlockedMessage =
      status === forwardStatusFor[props.order.status] ? getRegulatoryFindingTooltip(props.regulatoryFindings) : "";
    const blockerInformation = isCurrent ? "" : getOrderStatusBlockerMessage(props.order, status, blockedMessage);
    const { disabled, information } = getOrderStatusOptionState(
      isCurrent,
      isNeededActionAvailable,
      blockerInformation,
      regulatoryBlockedMessage,
    );
    return {
      status,
      information,
      label: termToText.get(status),
      value: status,
      disabled,
    };
  });

  if (selectOpen) {
    return (
      <Select
        value={props.order.status}
        onChange={v => {
          const status = v as OrderStatus | null;
          const actions: OrderAction[] = statusActions.find(sa => sa.status === status)?.actions ?? [];
          const allowedActions = actions.filter(a => availableActions.includes(a));
          if (allowedActions.length > 1) {
            throw new Error("Should only be one allowed Order Action");
          }
          const action = allowedActions[0];

          let activity: OrderActivityInputType | undefined = undefined;
          if (status && status !== props.order.status && action) {
            activity = {
              orderId: props.order.id,
              orderNo: props.order.orderNo,
              action,
              regulatoryFindings: action === OrderAction.SUBMIT ? props.regulatoryFindings?.findings : undefined,
            };
          }
          props.setOrderActivity(activity);
          setSelectOpen(false);
        }}
        initiallyOpened
        autoFocus
        onBlur={() => setSelectOpen(false)}
        itemComponent={OrderStatusSelectItem}
        data={selectOptions}
      />
    );
  }

  return (
    <Button color={statusToColors[props.order.status]} variant="subtle" onClick={() => setSelectOpen(true)}>
      <OrderStatusDisplay status={props.order.status} />
      <IconSelector size={16} />
    </Button>
  );
};

// Re-export so UI modules can consume the enum without a direct @prisma/client import in pages.
export { OrderStatus } from "@prisma/client";
